import { SenderSource } from '@enuan/chat__utils';
import classNames from 'classnames';
import { styled } from '@mui/material/styles';
import React, { useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { JSX } from 'react';
import { LongPressEventType, useLongPress } from 'use-long-press';
import { messageMessages } from 'src/components/message/message.i18n';
import { genericMessages } from 'src/generic.i18n';
import { IClientMessage, IClientMessageMeta } from 'src/interfaces';
import { UnreadMessageIcon } from './UnreadMessageIcon';
import { configurationContext } from 'src/contexts/configuration/context';

/**
 * MessageFooter props.
 * @typedef {Object} IMessageFooterProps
 * @property {React.ReactNode} [customFooter] - Replaces the default footer content
 * @property {boolean} [hideTime] - Hides the send time
 * @property {boolean} [hideRead] - Hides the unread message icon
 * @property {() => void} [onResend] - Callback to retry sending on failure
 * @property {number} [timestamp] - Message timestamp for displaying the time
 * @property {boolean} [sent] - Send status (false = failed, shows "Retry")
 * @property {boolean} [read] - Whether the message has been read by the user
 * @property {SenderSource} [sender] - Sender (retry is shown only for USER messages)
 * @property {() => void} [onDebugLongPress] - Callback fired on long-press on the time element (enables debug in MessageExtra)
 */
export type IMessageFooterProps = Omit<
	JSX.IntrinsicElements['footer'],
	'children'
> & {
	customFooter?: React.ReactNode;
	hideTime?: boolean;
	hideRead?: boolean;
	onResend?: () => void;
	onDebugLongPress?: () => void;
	timestamp?: IClientMessage['timestamp'];
	sent?: IClientMessageMeta['sent'];
	read?: IClientMessageMeta['read'];
	sender?: IClientMessage['sender'];
};

/**
 * Message footer: time, send/read status and "Retry send" action.
 *
 * **Behaviour:**
 * - If `customFooter` is provided, it is rendered instead of the default content.
 * - Otherwise renders in order: (1) when the message is sending (sent true, no timestamp), the "Sending…" label;
 *   (2) when timestamp exists and hideTime is false, the formatted time via MessageFooterTime (uses datetimes.message_sent config);
 *   (3) when send failed (sent === false) and sender is USER, a "Retry send" link that calls onResend;
 *   (4) when the message is unread and hideRead is false, the UnreadMessageIcon.
 * - Positioned in the Message grid with gridArea: 'footer'.
 *
 * @param {IMessageFooterProps} props - customFooter, hideTime, hideRead, onResend, timestamp, sent, read, sender
 * @returns {JSX.Element} Footer element with time, status and optional retry/unread icon
 */
export const MessageFooter = styled(
	({
		customFooter,
		hideTime = false,
		hideRead = false,
		onResend,
		onDebugLongPress,
		timestamp,
		sent,
		read,
		sender,
		className,
		...props
	}: IMessageFooterProps) => (
		<footer
			className={classNames('message__footer', className)}
			{...props}
		>
			{customFooter ?? (
				<MessageFooterContent
					footerHideTime={hideTime}
					footerHideRead={hideRead}
					onResend={onResend}
					onDebugLongPress={onDebugLongPress}
					timestamp={timestamp}
					sent={sent}
					read={read}
					sender={sender}
				/>
			)}
		</footer>
	),
	{ label: 'MessageFooter', target: 'MessageFooter-root' },
)({
	gridArea: 'footer',
	display: 'flex',
	fontFamily: 'Roboto, sans-serif',
	fontSize: '1em',
	fontWeight: 'lighter',
	color: 'var(--message__footer_color)',
	alignItems: 'center',
});

type IMessageFooterContentProps = {
	footerHideTime: boolean;
	footerHideRead: boolean;
	onResend?: () => void;
	onDebugLongPress?: () => void;
	timestamp?: IClientMessage['timestamp'];
	sent?: IClientMessageMeta['sent'];
	read?: IClientMessageMeta['read'];
	sender?: IClientMessage['sender'];
};

const MessageFooterContent = ({
	footerHideTime,
	footerHideRead,
	onResend,
	onDebugLongPress,
	timestamp,
	sent,
	read,
	sender,
}: IMessageFooterContentProps) => {
	const intl = useIntl();

	const showRead = sender !== SenderSource.USER && !read && !footerHideRead;
	const showTime =
		sender !== SenderSource.USER && !!timestamp && !footerHideTime;
	const showSending = sender === SenderSource.USER && !timestamp && !!sent;

	return (
		<>
			{showSending && (
				<span className="message__sending Message-sending">
					{intl.formatMessage(messageMessages.message__sending)}
				</span>
			)}
			{showTime && (
				<MessageFooterTime
					timestamp={timestamp}
					onDebugLongPress={onDebugLongPress}
				/>
			)}
			{sent === false && sender === SenderSource.USER && (
				<RetrySendSpan
					className="message__retry_send Message-retry_send"
					onClick={onResend}
				>
					{intl.formatMessage(messageMessages.message__retry_send)}
				</RetrySendSpan>
			)}
			{showRead && (
				<UnreadMessageIcon title={intl.formatMessage(genericMessages.unread)} />
			)}
		</>
	);
};

type IMessageFooterTimeProps = {
	timestamp: IClientMessage['timestamp'];
	onDebugLongPress?: () => void;
};

const MessageFooterTime = ({
	timestamp,
	onDebugLongPress,
}: IMessageFooterTimeProps) => {
	const intl = useIntl();
	const { localization } = useContext(configurationContext);
	const { time: timeFormat } = localization?.datetimes?.message_sent ?? {};
	const footerTime = useMemo(() => {
		if (!timestamp) return null;
		const date = new Date(timestamp);
		return intl.formatDate(date, {
			dateStyle: undefined,
			timeStyle: timeFormat ?? 'short',
		});
	}, [intl, timestamp, timeFormat]);

	const longPressBind = useLongPress(() => onDebugLongPress?.(), {
		detect: LongPressEventType.Pointer,
	});

	if (!footerTime) return null;
	return (
		<time className="message__time Message-time" {...longPressBind}>
			{footerTime}
		</time>
	);
};

const RetrySendSpan = styled('span')({
	color: 'var(--message__retry-send_color)',
	cursor: 'pointer',
});
